import React, { useEffect, useState, useCallback } from "react";
import api, { formatIDR, formatDate, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Printer, FilePdf, FileXls, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => new Date().toISOString().slice(0, 8) + "01";

export function useDownload() {
  return useCallback(async (path, filename) => {
    try {
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Gagal unduh");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success("Unduhan dimulai");
    } catch (e) { toast.error(e.message || "Gagal unduh"); }
  }, []);
}

export function usePdfPreview() {
  const [pv, setPv] = useState(null);
  const open = useCallback(async (path, filename) => {
    try {
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Gagal membuat PDF");
      const url = URL.createObjectURL(new Blob([await resp.blob()], { type: "application/pdf" }));
      setPv({ url, filename });
    } catch (e) { toast.error(e.message || "Gagal membuat PDF"); }
  }, []);
  const close = () => { if (pv) URL.revokeObjectURL(pv.url); setPv(null); };
  const dialog = <PdfPreviewDialog pv={pv} onClose={close} />;
  return [open, dialog];
}

function PdfPreviewDialog({ pv, onClose }) {
  const save = () => { const a = document.createElement("a"); a.href = pv.url; a.download = pv.filename; a.click(); };
  const print = () => { const w = document.getElementById("adv-pdf-iframe"); w?.contentWindow?.print(); };
  return (
    <Dialog open={!!pv} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-5xl h-[92vh] flex flex-col p-0 gap-0" data-testid="adv-pdf-preview-dialog">
        <DialogHeader className="shrink-0 border-b border-slate-200 px-6 py-3 flex flex-row items-center justify-between gap-3">
          <DialogTitle>Pratinjau PDF — {pv?.filename}</DialogTitle>
          <div className="flex gap-2 pr-6">
            <Button variant="outline" size="sm" onClick={print} data-testid="adv-pdf-print-btn"><Printer size={14} className="mr-1" />Cetak</Button>
            <Button size="sm" onClick={save} data-testid="adv-pdf-download-btn"><DownloadSimple size={14} className="mr-1" />Unduh</Button>
          </div>
        </DialogHeader>
        {pv && <iframe id="adv-pdf-iframe" src={pv.url} title="pdf-preview" className="w-full flex-1 border-0" data-testid="adv-pdf-iframe" />}
      </DialogContent>
    </Dialog>
  );
}

function ExportBar({ onPdf, onXlsx, onPrint }) {
  return (
    <div className="flex flex-wrap items-center gap-2 print:hidden">
      <Button variant="outline" size="sm" onClick={onPrint} data-testid="print-btn"><Printer size={14} className="mr-1" />Cetak</Button>
      <Button variant="outline" size="sm" onClick={onPdf} data-testid="export-pdf-btn" className="bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100"><FilePdf size={14} className="mr-1" />PDF</Button>
      <Button variant="outline" size="sm" onClick={onXlsx} data-testid="export-xlsx-btn" className="bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"><FileXls size={14} className="mr-1" />Excel</Button>
    </div>
  );
}

function SubsidiaryPanel({ mode, dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const label = mode === "ar" ? "Piutang" : "Hutang";
  const endpoint = mode === "ar" ? "/reports/ar-subsidiary" : "/reports/ap-subsidiary";
  const download = useDownload();
  const [preview, previewDialog] = usePdfPreview();
  const load = useCallback(() => {
    api.get(`${endpoint}?date_from=${dateFrom}&date_to=${dateTo}`).then(r => setData(r.data)).catch(() => setData(null));
  }, [endpoint, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const q = `date_from=${dateFrom}&date_to=${dateTo}`;
  return (
    <div className="space-y-4" data-testid={`subsidiary-${mode}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-slate-500">Buku Besar Pembantu {label} — saldo per pelanggan/vendor dengan saldo berjalan.</div>
        <ExportBar
          onPrint={() => window.print()}
          onPdf={() => preview(`${endpoint}?${q}&format=pdf`, `buku_besar_pembantu_${label.toLowerCase()}.pdf`)}
          onXlsx={() => download(`${endpoint}?${q}&format=xlsx`, `buku_besar_pembantu_${label.toLowerCase()}.xlsx`)}
        />
      </div>
      {previewDialog}
      {!data && <div className="text-slate-500 text-sm">Memuat…</div>}
      {data && data.partners.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-8 text-center text-slate-500 text-sm">Tidak ada saldo {label.toLowerCase()} di periode ini.</div>
      )}
      {data && data.partners.map((p) => (
        <div key={p.partner_id} className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={`partner-${p.partner_id}`}>
          <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
            <div>
              <div className="font-medium">{p.partner_name}</div>
              <div className="text-xs text-slate-500">Saldo Awal: <span className="tabular-nums">{formatIDR(p.opening_balance)}</span> · Saldo Akhir: <span className="tabular-nums font-semibold text-slate-900">{formatIDR(p.closing_balance)}</span></div>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tanggal</TableHead>
                <TableHead>Nomor</TableHead>
                <TableHead>Keterangan</TableHead>
                <TableHead className="text-right">Debit (Rp)</TableHead>
                <TableHead className="text-right">Kredit (Rp)</TableHead>
                <TableHead className="text-right">Saldo (Rp)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="bg-slate-50/40">
                <TableCell colSpan={5} className="text-xs italic text-slate-500">Saldo Awal per {dateFrom}</TableCell>
                <TableCell className="tabular-nums text-right font-semibold">{formatIDR(p.opening_balance)}</TableCell>
              </TableRow>
              {p.transactions.map((t, i) => (
                <TableRow key={i}>
                  <TableCell>{formatDate(t.date)}</TableCell>
                  <TableCell className="font-mono-plex text-xs">{t.number || "—"}</TableCell>
                  <TableCell className="text-sm">{t.description}</TableCell>
                  <TableCell className="tabular-nums text-right text-emerald-700">{t.debit > 0 ? formatIDR(t.debit) : "—"}</TableCell>
                  <TableCell className="tabular-nums text-right text-rose-700">{t.credit > 0 ? formatIDR(t.credit) : "—"}</TableCell>
                  <TableCell className="tabular-nums text-right font-semibold">{formatIDR(t.balance)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-slate-100 border-t-2 border-slate-900 font-semibold">
                <TableCell colSpan={3}>Total & Saldo Akhir</TableCell>
                <TableCell className="tabular-nums text-right">{formatIDR(p.total_debit)}</TableCell>
                <TableCell className="tabular-nums text-right">{formatIDR(p.total_credit)}</TableCell>
                <TableCell className="tabular-nums text-right">{formatIDR(p.closing_balance)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      ))}
      {data && data.partners.length > 0 && (
        <div className="bg-slate-900 text-white rounded-md p-4 flex justify-between items-center" data-testid={`${mode}-grand-total`}>
          <div className="text-sm font-semibold">GRAND TOTAL Seluruh {mode === "ar" ? "Pelanggan" : "Vendor"} ({data.partners.length})</div>
          <div className="flex flex-wrap gap-4 sm:gap-8 text-sm tabular-nums">
            <div><div className="text-xs text-slate-400">Saldo Awal</div>{formatIDR(data.grand_opening)}</div>
            <div><div className="text-xs text-slate-400">Debit</div>{formatIDR(data.grand_debit)}</div>
            <div><div className="text-xs text-slate-400">Kredit</div>{formatIDR(data.grand_credit)}</div>
            <div><div className="text-xs text-slate-400">Saldo Akhir</div><span className="font-semibold">{formatIDR(data.grand_closing)}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProjectPnLPanel({ dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const download = useDownload();
  const [preview, previewDialog] = usePdfPreview();
  useEffect(() => {
    api.get(`/reports/pnl-per-project?date_from=${dateFrom}&date_to=${dateTo}`).then(r => setData(r.data)).catch(() => setData(null));
  }, [dateFrom, dateTo]);
  const q = `date_from=${dateFrom}&date_to=${dateTo}`;
  return (
    <div className="space-y-4" data-testid="pnl-project">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">Laba Rugi per Proyek — Pendapatan, HPP, Beban, Laba Bersih.</div>
        <ExportBar
          onPrint={() => window.print()}
          onPdf={() => preview(`/reports/pnl-per-project?${q}&format=pdf`, "laba_rugi_per_proyek.pdf")}
          onXlsx={() => download(`/reports/pnl-per-project?${q}&format=xlsx`, "laba_rugi_per_proyek.xlsx")}
        />
      </div>
      {previewDialog}
      {!data && <div className="text-slate-500 text-sm">Memuat…</div>}
      {data && (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          <Table className="min-w-[1100px] [&_td]:align-top [&_td.tabular-nums]:whitespace-nowrap [&_th]:whitespace-nowrap">
            <TableHeader>
              <TableRow>
                <TableHead>Kode</TableHead>
                <TableHead>Proyek</TableHead>
                <TableHead>Tgl Dibuat</TableHead>
                <TableHead>Pelanggan</TableHead>
                <TableHead className="text-right">Pendapatan</TableHead>
                <TableHead className="text-right">HPP</TableHead>
                <TableHead className="text-right">Laba Kotor</TableHead>
                <TableHead className="text-right">Margin %</TableHead>
                <TableHead className="text-right">Beban</TableHead>
                <TableHead className="text-right">Laba Bersih</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.projects.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-10 text-slate-500 text-sm">Tidak ada data proyek di periode ini.</TableCell></TableRow>
              ) : data.projects.map((p, i) => (
                <TableRow key={i} data-testid={`proj-row-${i}`}>
                  <TableCell className="font-mono-plex text-xs whitespace-nowrap">{p.project_code || "—"}</TableCell>
                  <TableCell className="font-medium min-w-[200px]">{p.project_name}<div className="text-xs text-slate-500">{p.sales_count} faktur jual · {p.purchase_count} faktur beli · {p.expense_count} beban</div></TableCell>
                  <TableCell className="text-xs whitespace-nowrap" data-testid={`proj-created-${i}`}>{p.project_created_date ? formatDate(p.project_created_date) : "—"}</TableCell>
                  <TableCell className="text-sm min-w-[150px]">{p.customer_name || "—"}</TableCell>
                  <TableCell className="tabular-nums text-right text-emerald-700">{formatIDR(p.revenue)}</TableCell>
                  <TableCell className="tabular-nums text-right text-slate-600">{formatIDR(p.cogs)}</TableCell>
                  <TableCell className="tabular-nums text-right font-semibold">{formatIDR(p.gross_profit)}</TableCell>
                  <TableCell className="tabular-nums text-right">{p.margin_pct.toFixed(2)}%</TableCell>
                  <TableCell className="tabular-nums text-right text-rose-700">{formatIDR(p.expense)}</TableCell>
                  <TableCell className={`tabular-nums text-right font-bold ${p.net_profit >= 0 ? "text-slate-900" : "text-rose-700"}`}>{formatIDR(p.net_profit)}</TableCell>
                </TableRow>
              ))}
              {data.projects.length > 0 && (
                <TableRow className="bg-slate-900 hover:bg-slate-900" data-testid="proj-totals">
                  <TableCell className="text-white font-bold" colSpan={4}>TOTAL</TableCell>
                  <TableCell className="tabular-nums text-right text-white font-bold">{formatIDR(data.total_revenue)}</TableCell>
                  <TableCell className="tabular-nums text-right text-white font-bold">{formatIDR(data.total_cogs)}</TableCell>
                  <TableCell className="tabular-nums text-right text-white font-bold">{formatIDR(data.total_gross)}</TableCell>
                  <TableCell></TableCell>
                  <TableCell className="tabular-nums text-right text-white font-bold">{formatIDR(data.total_expense)}</TableCell>
                  <TableCell className="tabular-nums text-right text-white font-bold">{formatIDR(data.total_net)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function SalesVsPurchasePanel({ dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const download = useDownload();
  const [preview, previewDialog] = usePdfPreview();
  useEffect(() => {
    api.get(`/reports/sales-vs-purchase?date_from=${dateFrom}&date_to=${dateTo}`).then(r => setData(r.data)).catch(() => setData(null));
  }, [dateFrom, dateTo]);
  const q = `date_from=${dateFrom}&date_to=${dateTo}`;
  return (
    <div className="space-y-4" data-testid="sales-vs-purchase">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">Penjualan vs Pembelian per Item — margin per barang/jasa.</div>
        <ExportBar
          onPrint={() => window.print()}
          onPdf={() => preview(`/reports/sales-vs-purchase?${q}&format=pdf`, "penjualan_vs_pembelian.pdf")}
          onXlsx={() => download(`/reports/sales-vs-purchase?${q}&format=xlsx`, "penjualan_vs_pembelian.xlsx")}
        />
      </div>
      {previewDialog}
      {!data && <div className="text-slate-500 text-sm">Memuat…</div>}
      {data && (
        <>
          <div className="grid md:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200 rounded-md p-4"><div className="text-xs uppercase text-slate-500">Total Penjualan</div><div className="mt-1 font-serif-display text-xl font-semibold tabular-nums text-emerald-700">{formatIDR(data.total_sold)}</div></div>
            <div className="bg-white border border-slate-200 rounded-md p-4"><div className="text-xs uppercase text-slate-500">Total HPP Terjual</div><div className="mt-1 font-serif-display text-xl font-semibold tabular-nums text-slate-600">{formatIDR(data.total_cogs)}</div></div>
            <div className="bg-white border border-slate-200 rounded-md p-4"><div className="text-xs uppercase text-slate-500">Margin (Rp)</div><div className="mt-1 font-serif-display text-xl font-semibold tabular-nums text-slate-900">{formatIDR(data.total_margin)}</div></div>
            <div className="bg-white border border-slate-200 rounded-md p-4"><div className="text-xs uppercase text-slate-500">Margin %</div><div className="mt-1 font-serif-display text-xl font-semibold tabular-nums">{data.total_margin_pct.toFixed(2)}%</div></div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Nama Barang/Jasa</TableHead>
                  <TableHead className="text-right">Qty Terjual</TableHead>
                  <TableHead className="text-right">Nilai Penjualan</TableHead>
                  <TableHead className="text-right">Qty Dibeli</TableHead>
                  <TableHead className="text-right">Rata² HPP</TableHead>
                  <TableHead className="text-right">HPP Terjual</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                  <TableHead className="text-right">Margin %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-500 text-sm">Tidak ada aktivitas item di periode ini.</TableCell></TableRow>
                ) : data.items.map((r, i) => (
                  <TableRow key={i} data-testid={`item-row-${i}`}>
                    <TableCell className="font-mono-plex text-xs">{r.sku || "—"}</TableCell>
                    <TableCell className="font-medium">{r.product_name}</TableCell>
                    <TableCell className="tabular-nums text-right">{r.sold_qty}</TableCell>
                    <TableCell className="tabular-nums text-right text-emerald-700">{formatIDR(r.sold_amount)}</TableCell>
                    <TableCell className="tabular-nums text-right">{r.purchased_qty}</TableCell>
                    <TableCell className="tabular-nums text-right">{formatIDR(r.avg_cost)}</TableCell>
                    <TableCell className="tabular-nums text-right text-slate-600">{formatIDR(r.cogs_of_sold)}</TableCell>
                    <TableCell className={`tabular-nums text-right font-semibold ${r.margin >= 0 ? "text-slate-900" : "text-rose-700"}`}>{formatIDR(r.margin)}</TableCell>
                    <TableCell className="tabular-nums text-right">{r.margin_pct.toFixed(2)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

const AGING_STYLE = {
  current: "bg-emerald-50 text-emerald-700",
  d1_30: "bg-amber-50 text-amber-700",
  d31_60: "bg-orange-100 text-orange-700",
  d61_90: "bg-rose-100 text-rose-700",
  over90: "bg-rose-600 text-white",
};

function AgingPanel({ mode, asOf }) {
  const [data, setData] = useState(null);
  const label = mode === "ar" ? "Piutang" : "Hutang";
  const endpoint = mode === "ar" ? "/reports/ar-aging" : "/reports/ap-aging";
  const download = useDownload();
  const [preview, previewDialog] = usePdfPreview();
  useEffect(() => {
    api.get(`${endpoint}?as_of=${asOf}`).then(r => setData(r.data)).catch(() => setData(null));
  }, [endpoint, asOf]);
  const q = `as_of=${asOf}`;
  return (
    <div className="space-y-4" data-testid={`aging-${mode}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-slate-500">Umur {label} per tanggal <span className="font-medium text-slate-700">{formatDate(asOf)}</span> (kolom "Sampai Tanggal"). Warna = tingkat urgensi penagihan.</div>
        <ExportBar
          onPrint={() => window.print()}
          onPdf={() => preview(`${endpoint}?${q}&format=pdf`, `aging_${label.toLowerCase()}.pdf`)}
          onXlsx={() => download(`${endpoint}?${q}&format=xlsx`, `aging_${label.toLowerCase()}.xlsx`)}
        />
      </div>
      {previewDialog}
      {!data && <div className="text-slate-500 text-sm">Memuat…</div>}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            {(data.bucket_labels || []).map((b) => (
              <div key={b.key} className={`rounded-md border border-slate-200 p-3 ${AGING_STYLE[b.key]}`} data-testid={`aging-card-${b.key}`}>
                <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80">{b.label}</div>
                <div className="mt-1 font-serif-display text-lg font-semibold tabular-nums">{formatIDR(data.grand[b.key] || 0)}</div>
              </div>
            ))}
            <div className="rounded-md p-3 bg-slate-900 text-white" data-testid="aging-card-total">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-300">Total Outstanding</div>
              <div className="mt-1 font-serif-display text-lg font-semibold tabular-nums">{formatIDR(data.grand_total)}</div>
            </div>
          </div>
          {data.partners.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-8 text-center text-slate-500 text-sm">Tidak ada {label.toLowerCase()} outstanding per tanggal ini.</div>
          )}
          {data.partners.map((p) => (
            <div key={p.partner_id} className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={`aging-partner-${p.partner_id}`}>
              <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
                <div className="font-medium">{p.partner_name}</div>
                <div className="text-sm tabular-nums font-semibold">{formatIDR(p.total)}</div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>No. Faktur</TableHead>
                    <TableHead>Tgl Faktur</TableHead>
                    <TableHead>Jatuh Tempo</TableHead>
                    <TableHead className="text-right">Hari Lewat</TableHead>
                    {data.bucket_labels.map((b) => <TableHead key={b.key} className="text-right">{b.label}</TableHead>)}
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {p.invoices.map((t, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono-plex text-xs">{t.number}</TableCell>
                      <TableCell className="text-xs">{formatDate(t.date)}</TableCell>
                      <TableCell className="text-xs">{formatDate(t.due_date)}</TableCell>
                      <TableCell className="text-right">
                        {t.days_overdue > 0
                          ? <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${AGING_STYLE[t.bucket]}`}>{t.days_overdue} hari</span>
                          : <span className="text-xs text-slate-400">—</span>}
                      </TableCell>
                      {data.bucket_labels.map((b) => (
                        <TableCell key={b.key} className={`tabular-nums text-right text-xs ${t.bucket === b.key ? `${AGING_STYLE[b.key]} font-semibold` : "text-slate-300"}`}>
                          {t.bucket === b.key ? formatIDR(t.balance) : ""}
                        </TableCell>
                      ))}
                      <TableCell className="tabular-nums text-right font-semibold text-sm">{formatIDR(t.balance)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-100 border-t-2 border-slate-900 font-semibold">
                    <TableCell colSpan={4}>Subtotal {p.partner_name}</TableCell>
                    {data.bucket_labels.map((b) => (
                      <TableCell key={b.key} className="tabular-nums text-right text-xs">{p.buckets[b.key] ? formatIDR(p.buckets[b.key]) : ""}</TableCell>
                    ))}
                    <TableCell className="tabular-nums text-right">{formatIDR(p.total)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function PoSummaryPanel({ dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const download = useDownload();
  const [preview, previewDialog] = usePdfPreview();
  useEffect(() => {
    api.get(`/reports/po-summary?date_from=${dateFrom}&date_to=${dateTo}`).then(r => setData(r.data)).catch(() => setData(null));
  }, [dateFrom, dateTo]);
  const q = `date_from=${dateFrom}&date_to=${dateTo}`;
  return (
    <div className="space-y-4" data-testid="po-summary">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-slate-500">Rekap per No. PO pelanggan — pesanan, pengiriman, faktur, dan sisa tagihan. Klik baris untuk rincian faktur.</div>
        <ExportBar
          onPrint={() => window.print()}
          onPdf={() => preview(`/reports/po-summary?${q}&format=pdf`, "ringkasan_tagihan_per_po.pdf")}
          onXlsx={() => download(`/reports/po-summary?${q}&format=xlsx`, "ringkasan_tagihan_per_po.xlsx")}
        />
      </div>
      {previewDialog}
      {!data && <div className="text-slate-500 text-sm">Memuat…</div>}
      {data && (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>No. PO</TableHead><TableHead>Pelanggan</TableHead><TableHead>Pesanan</TableHead><TableHead>Pengiriman</TableHead><TableHead>Faktur</TableHead>
                <TableHead className="text-right">Nilai Pesanan</TableHead><TableHead className="text-right">Total Faktur</TableHead><TableHead className="text-right">Dibayar</TableHead><TableHead className="text-right">Sisa Tagihan</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.pos.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-500 text-sm">Tidak ada dokumen ber-No. PO di periode ini.</TableCell></TableRow>
              ) : data.pos.map((g, i) => (
                <React.Fragment key={i}>
                  <TableRow className="cursor-pointer hover:bg-amber-50/50" onClick={() => setOpen({ ...open, [i]: !open[i] })} data-testid={`po-row-${i}`}>
                    <TableCell><span className="font-mono-plex text-xs px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{g.customer_po_no}</span></TableCell>
                    <TableCell className="font-medium text-sm">{g.partner_name}</TableCell>
                    <TableCell className="font-mono-plex text-xs">{g.orders.map(o => o.number).join(", ") || "—"}</TableCell>
                    <TableCell className="font-mono-plex text-xs">{g.deliveries.map(o => o.number).join(", ") || "—"}</TableCell>
                    <TableCell className="font-mono-plex text-xs">{g.invoices.map(o => o.number).join(", ") || "—"}</TableCell>
                    <TableCell className="tabular-nums text-right">{formatIDR(g.total_order)}</TableCell>
                    <TableCell className="tabular-nums text-right">{formatIDR(g.total_invoiced)}{g.uninvoiced > 0 && <div className="text-[10px] text-amber-700">Belum difakturkan {formatIDR(g.uninvoiced)}</div>}</TableCell>
                    <TableCell className="tabular-nums text-right text-emerald-700">{formatIDR(g.total_paid)}</TableCell>
                    <TableCell className={`tabular-nums text-right font-semibold ${g.balance > 0 ? "text-rose-700" : "text-slate-900"}`} data-testid={`po-balance-${i}`}>{formatIDR(g.balance)}</TableCell>
                  </TableRow>
                  {open[i] && g.invoices.map((inv) => (
                    <TableRow key={inv.number} className="bg-slate-50/60 text-xs" data-testid={`po-inv-${i}-${inv.number}`}>
                      <TableCell colSpan={4} className="pl-8 text-slate-500">↳ Faktur <span className="font-mono-plex">{inv.number}</span> · {formatDate(inv.date)} · jatuh tempo {formatDate(inv.due_date)}</TableCell>
                      <TableCell className="capitalize">{inv.status}</TableCell>
                      <TableCell></TableCell>
                      <TableCell className="tabular-nums text-right">{formatIDR(inv.total)}</TableCell>
                      <TableCell className="tabular-nums text-right">{formatIDR(inv.paid)}</TableCell>
                      <TableCell className="tabular-nums text-right">{formatIDR(inv.balance)}</TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
              {data.pos.length > 0 && (
                <TableRow className="bg-slate-900 hover:bg-slate-900" data-testid="po-totals">
                  <TableCell colSpan={5} className="text-white font-bold">TOTAL</TableCell>
                  <TableCell className="tabular-nums text-right text-white font-bold">{formatIDR(data.total_order)}</TableCell>
                  <TableCell className="tabular-nums text-right text-white font-bold">{formatIDR(data.total_invoiced)}</TableCell>
                  <TableCell className="tabular-nums text-right text-white font-bold">{formatIDR(data.total_paid)}</TableCell>
                  <TableCell className="tabular-nums text-right text-white font-bold">{formatIDR(data.total_balance)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default function AdvancedReports() {
  const [tab, setTab] = useState("ar");
  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(today());

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl" data-testid="advanced-reports-page">
      <div className="flex justify-between items-baseline flex-wrap gap-3">
        <div>
          <h1 className="font-serif-display text-4xl font-medium text-slate-900">Laporan Lanjutan</h1>
          <p className="text-sm text-slate-500 mt-1">Buku Besar Pembantu Piutang &amp; Hutang, Laba Rugi per Proyek, dan Penjualan vs Pembelian.</p>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md p-4 flex items-end gap-3 flex-wrap print:hidden">
        <div><Label>Dari Tanggal</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="adv-date-from" /></div>
        <div><Label>Sampai Tanggal</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="adv-date-to" /></div>
        <div className="ml-auto text-xs text-slate-500">Periode: {formatDate(dateFrom)} — {formatDate(dateTo)}</div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-4">
        <TabsList className="print:hidden h-auto flex-wrap justify-start">
          <TabsTrigger value="ar" data-testid="tab-ar">Buku Besar Pembantu Piutang</TabsTrigger>
          <TabsTrigger value="ap" data-testid="tab-ap">Buku Besar Pembantu Hutang</TabsTrigger>
          <TabsTrigger value="aging-ar" data-testid="tab-aging-ar">Aging Piutang</TabsTrigger>
          <TabsTrigger value="aging-ap" data-testid="tab-aging-ap">Aging Hutang</TabsTrigger>
          <TabsTrigger value="project" data-testid="tab-project">Laba Rugi per Proyek</TabsTrigger>
          <TabsTrigger value="sales-vs-purchase" data-testid="tab-svp">Penjualan vs Pembelian</TabsTrigger>
          <TabsTrigger value="po-summary" data-testid="tab-po-summary">Tagihan per PO</TabsTrigger>
        </TabsList>
        <TabsContent value="ar" className="mt-4"><SubsidiaryPanel mode="ar" dateFrom={dateFrom} dateTo={dateTo} /></TabsContent>
        <TabsContent value="ap" className="mt-4"><SubsidiaryPanel mode="ap" dateFrom={dateFrom} dateTo={dateTo} /></TabsContent>
        <TabsContent value="aging-ar" className="mt-4"><AgingPanel mode="ar" asOf={dateTo} /></TabsContent>
        <TabsContent value="aging-ap" className="mt-4"><AgingPanel mode="ap" asOf={dateTo} /></TabsContent>
        <TabsContent value="project" className="mt-4"><ProjectPnLPanel dateFrom={dateFrom} dateTo={dateTo} /></TabsContent>
        <TabsContent value="sales-vs-purchase" className="mt-4"><SalesVsPurchasePanel dateFrom={dateFrom} dateTo={dateTo} /></TabsContent>
        <TabsContent value="po-summary" className="mt-4"><PoSummaryPanel dateFrom={dateFrom} dateTo={dateTo} /></TabsContent>
      </Tabs>
    </div>
  );
}
